import { getStore } from '../../../../state/configure_store'
import { refreshTokenOnce } from '../../../refresh_token'
import { setUser } from '../../../../state/slices/user_slice'
import { logout } from '../../../../state/actions/constants'
import { setResourceAuthorization, setResourceAuthorizations } from '../../../../state/slices/resource_authorization_slice'
import { setTerms } from '../../../../state/slices/terms_slice'
import { setToken } from '../../../../state/slices/token_slice'
import {
  createAuthSessionSnapshot,
  getAuthSessionSnapshotStatus
} from '../../../../utils/auth_session'
import { isInvalidRefreshTokenError } from '../../../../utils/refresh_token_error'
import * as Sentry from '@sentry/react'

const debugAuthRefresh = (message: string, meta?: unknown) => {
  if (process.env.NODE_ENV === 'production') return

  console.debug(`[auth-refresh] ${message}`, meta ?? '')
}

const recordAutomaticLogoutBreadcrumb = (
  reason: 'interceptor_no_refresh_snapshot' | 'interceptor_refresh_invalid',
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'warning',
    message: 'Automatic logout dispatched',
    data: {
      reason,
      source: 'refreshTokenInterceptor',
      ...data
    }
  })
}

const getFailedRequestStatus = (failedRequest: unknown) =>
  (failedRequest as { response?: { status?: number } })?.response?.status

const onRefreshInvalid = (
  failedRequest: unknown,
  reason: 'interceptor_no_refresh_snapshot' | 'interceptor_refresh_invalid',
  data?: Record<string, unknown>
) => {
  const store = getStore()

  debugAuthRefresh('refresh token invalid; logging out')
  recordAutomaticLogoutBreadcrumb(reason, {
    failedRequestStatus: getFailedRequestStatus(failedRequest),
    ...data
  })
  store.dispatch(logout())

  return Promise.reject(failedRequest)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const refreshTokenInterceptor = async (failedRequest: any) => {
  const store = getStore()
  const state = store.getState()
  const snapshot = createAuthSessionSnapshot(state)

  if (!snapshot) {
    debugAuthRefresh('no refresh token available')

    return onRefreshInvalid(failedRequest, 'interceptor_no_refresh_snapshot')
  }

  try {
    debugAuthRefresh('refreshing access token')

    const response = await refreshTokenOnce(snapshot.refreshToken)
    const currentState = store.getState()
    const status = getAuthSessionSnapshotStatus(currentState, snapshot)

    if (status === 'logged_out') {
      debugAuthRefresh('session changed during refresh; user is logged out')

      return Promise.reject(failedRequest)
    }

    if (status === 'superseded') {
      const currentAccessToken = currentState.token.token?.accessToken

      if (currentAccessToken) {
        debugAuthRefresh('token already refreshed by another request; retrying original request')
        failedRequest.response.config.headers['Authorization'] = `Bearer ${currentAccessToken}`

        return Promise.resolve()
      }

      debugAuthRefresh('token was superseded but no access token is available')

      return Promise.reject(failedRequest)
    }

    if (response.data && response.data.jsonWebToken && response.data.user) {
      store.dispatch(setToken(response.data.jsonWebToken))
      store.dispatch(setUser(response.data.user))
      store.dispatch(setTerms(response.data.user.term))
      store.dispatch(setResourceAuthorization(response.data.user.currentResourceAuthorization))
      store.dispatch(setResourceAuthorizations(response.data.user.resourceAuthorizations ?? []))

      failedRequest.response.config.headers['Authorization'] = `Bearer ${response.data.jsonWebToken.accessToken}`

      debugAuthRefresh('access token refreshed; retrying original request')

      return Promise.resolve()
    }

    debugAuthRefresh('refresh response did not include a usable token')

    return Promise.reject(failedRequest)
  } catch (error) {
    if (isInvalidRefreshTokenError(error)) {
      return onRefreshInvalid(failedRequest, 'interceptor_refresh_invalid', {
        refreshStatus: (error as { response?: { status?: number } })?.response?.status
      })
    }

    debugAuthRefresh('refresh request failed', error)

    return Promise.reject(failedRequest)
  }
}
